import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { PinDialog } from '@/components/ui/PinDialog';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { api } from '@/lib/api';
import { call } from '@/api/desk';
import { FileUpload } from '@/components/kit/upload';
import { DataTable, DateInput, Filters, KV, Link2, Modal, Notice, PageHeader, Pager, Search, Sel, Tabs, arr, dOnly, dt, errMsg, inputCls, money, newKey, useGet, useList } from '@/features/shared/ops/kit';
import { RolesPanel } from '@/features/desk/pages/StaffPage';

const Row = ({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) => (
  <label className="block text-sm"><span className="block mb-1 font-medium text-slate-600">{label}</span>{children}{hint && <span className="block text-xs text-slate-400 mt-1">{hint}</span>}</label>
);

/* ---------- profile ---------- */
function ProfileForm() {
  const qc = useQueryClient();
  const q = useGet<any>('owner-profile', '/school/profile');
  const [f, setF] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (q.data) setF({ name: q.data.name ?? '', email: q.data.email ?? '', phone: q.data.phone ?? '', address: q.data.address ?? '', city: q.data.city ?? '', state: q.data.state ?? '', school_type: q.data.school_type ?? '' });
  }, [q.data]);
  const set = (k: string) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  async function save() {
    setBusy(true);
    setMsg(null);
    try {
      const body = { name: f.name.trim(), email: f.email.trim() || null, phone: f.phone.trim() || null, address: f.address.trim() || null, city: f.city.trim() || null, state: f.state.trim() || null, ...(f.school_type ? { school_type: f.school_type } : {}) };
      const r = await call('put', '/school/profile', { body });
      setMsg({ kind: 'ok', text: r.message ?? 'School profile updated.' });
      qc.invalidateQueries({ queryKey: ['owner-profile'] }); qc.invalidateQueries({ queryKey: ['auth-me-school'] });
    } catch (e) {
      setMsg({ kind: 'error', text: errMsg(e) });
    } finally {
      setBusy(false);
    }
  }
  if (q.isLoading) return <Card title="School profile"><p className="text-sm text-slate-500">Loading…</p></Card>;
  if (q.error) return <Card title="School profile"><Notice kind="error">{errMsg(q.error)}</Notice></Card>;
  return (
    <Card title="School profile">
      {msg && <Notice kind={msg.kind}>{msg.text}</Notice>}
      <div className="flex gap-4 text-sm text-slate-500 mb-4 flex-wrap">
        <span>Code <b className="text-slate-800">{q.data?.code}</b></span>
        <span>Status <StatusBadge status={q.data?.status ?? 'active'} /></span>
        <span>{q.data?.counts?.active_students ?? 0} active students · {q.data?.counts?.staff ?? 0} staff · {q.data?.counts?.classes ?? 0} classes</span>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <Row label="School name"><input className={inputCls} maxLength={120} value={f.name ?? ''} onChange={set('name')} /></Row>
        <Row label="School type"><select className={inputCls} value={f.school_type ?? ''} onChange={set('school_type')}><option value="">—</option><option value="nursery">Nursery</option><option value="primary">Primary</option><option value="secondary">Secondary</option><option value="mixed">Mixed</option></select></Row>
        <Row label="Email"><input className={inputCls} type="email" value={f.email ?? ''} onChange={set('email')} /></Row>
        <Row label="Phone"><input className={inputCls} maxLength={20} value={f.phone ?? ''} onChange={set('phone')} /></Row>
        <div className="md:col-span-2"><Row label="Address"><input className={inputCls} maxLength={255} value={f.address ?? ''} onChange={set('address')} /></Row></div>
        <Row label="City"><input className={inputCls} maxLength={80} value={f.city ?? ''} onChange={set('city')} /></Row>
        <Row label="State"><input className={inputCls} maxLength={80} value={f.state ?? ''} onChange={set('state')} /></Row>
      </div>
      <div className="mt-4"><Button disabled={busy || !f.name?.trim()} onClick={save}>{busy ? 'Saving…' : 'Save profile'}</Button></div>
    </Card>
  );
}

/* ---------- branding (multipart: logo + colours) ---------- */
function Branding() {
  const qc = useQueryClient();
  const q = useGet<any>('owner-profile', '/school/profile');
  const [primary, setPrimary] = useState('#0f766e');
  const [accent, setAccent] = useState('#f59e0b');
  const [logo, setLogo] = useState<File | null>(null);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (q.data?.primary_color) setPrimary(q.data.primary_color);
    if (q.data?.accent_color) setAccent(q.data.accent_color);
  }, [q.data]);
  const hex = /^#[0-9A-Fa-f]{6}$/;
  async function save() {
    setMsg(null);
    if (!hex.test(primary) || !hex.test(accent)) return setMsg({ kind: 'error', text: 'Colours must look like #RRGGBB.' });
    if (logo && logo.size > 2 * 1024 * 1024) return setMsg({ kind: 'error', text: 'The logo must be 2 MB or smaller.' });
    const fd = new FormData();
    fd.append('_method', 'PUT'); // multipart bodies are only parsed on POST; Laravel spoofs the PUT
    fd.append('primary_color', primary);
    fd.append('accent_color', accent);
    if (logo) fd.append('logo', logo);
    setBusy(true);
    try {
      const r = await api.post('/school/branding', fd);
      setMsg({ kind: 'ok', text: r.data?.message ?? 'Branding saved.' });
      setLogo(null);
      if (file.current) file.current.value = '';
      qc.invalidateQueries({ queryKey: ['owner-profile'] }); qc.invalidateQueries({ queryKey: ['auth-me-school'] });
    } catch (e) {
      setMsg({ kind: 'error', text: errMsg(e) });
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card title="Branding">
      {msg && <Notice kind={msg.kind}>{msg.text}</Notice>}
      <div className="grid md:grid-cols-3 gap-4 text-sm">
        <Row label="Logo" hint="PNG, JPG or WEBP up to 2 MB">
          <div className="flex items-center gap-3">
            {q.data?.logo_path && !logo && <span className="text-slate-500">Current: {String(q.data.logo_path).split('/').pop()}</span>}
            <input ref={file} type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => setLogo(e.target.files?.[0] ?? null)} />
          </div>
        </Row>
        <Row label="Primary colour"><div className="flex gap-2"><input type="color" value={hex.test(primary) ? primary : '#000000'} onChange={(e) => setPrimary(e.target.value)} /><input className={inputCls} maxLength={7} value={primary} onChange={(e) => setPrimary(e.target.value)} /></div></Row>
        <Row label="Accent colour"><div className="flex gap-2"><input type="color" value={hex.test(accent) ? accent : '#000000'} onChange={(e) => setAccent(e.target.value)} /><input className={inputCls} maxLength={7} value={accent} onChange={(e) => setAccent(e.target.value)} /></div></Row>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Button disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save branding'}</Button>
        <span className="inline-flex gap-1"><span className="w-6 h-6 rounded" style={{ background: primary }} /><span className="w-6 h-6 rounded" style={{ background: accent }} /></span>
      </div>
    </Card>
  );
}

/* ---------- result theme ---------- */
function ResultTheme() {
  const qc = useQueryClient();
  const q = useGet<any>('owner-profile', '/school/profile');
  const [t, setT] = useState({ template: 'classic', show_position: true, show_class_average: true, show_attendance: true, header_note: '', principal_signature_path: '' as string | null });
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const r = q.data?.result_theme;
    if (r && typeof r === 'object') setT((x) => ({ ...x, ...r, header_note: r.header_note ?? '', principal_signature_path: r.principal_signature_path ?? null }));
  }, [q.data]);
  async function save() {
    setBusy(true);
    setMsg(null);
    try {
      const r = await call('put', '/school/result-theme', { body: { ...t, header_note: t.header_note?.trim() || null } });
      setMsg({ kind: 'ok', text: r.message ?? 'Result theme saved.' });
      qc.invalidateQueries({ queryKey: ['owner-profile'] }); qc.invalidateQueries({ queryKey: ['auth-me-school'] });
    } catch (e) {
      setMsg({ kind: 'error', text: errMsg(e) });
    } finally {
      setBusy(false);
    }
  }
  const check = (k: 'show_position' | 'show_class_average' | 'show_attendance', l: string) => (
    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!t[k]} onChange={(e) => setT({ ...t, [k]: e.target.checked })} /> {l}</label>
  );
  return (
    <Card title="Report card theme">
      {msg && <Notice kind={msg.kind}>{msg.text}</Notice>}
      <div className="grid md:grid-cols-2 gap-4">
        <Row label="Layout"><select className={inputCls} value={t.template} onChange={(e) => setT({ ...t, template: e.target.value })}><option value="classic">Classic</option><option value="modern">Modern</option><option value="compact">Compact</option></select></Row>
        <Row label="Header note"><input className={inputCls} maxLength={200} value={t.header_note ?? ''} onChange={(e) => setT({ ...t, header_note: e.target.value })} /></Row>
        <div className="space-y-2">{check('show_position', 'Show position in class')}{check('show_class_average', 'Show class average')}{check('show_attendance', 'Show attendance')}</div>
        <FileUpload label="Principal's signature" accept="image/png,image/jpeg,image/webp" value={t.principal_signature_path} onUploaded={(m) => setT({ ...t, principal_signature_path: m.path })} onClear={() => setT({ ...t, principal_signature_path: null })} />
      </div>
      <div className="mt-4"><Button disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save theme'}</Button></div>
    </Card>
  );
}

/* ---------- school settings (API_CHANGES §8) ---------- */
const DOC_FEES = [['transcript', 'Transcript'], ['testimonial', 'Testimonial'], ['statement_of_result', 'Statement of result'], ['certificate', 'Certificate']] as const;
function SchoolSettings({ section = 'rules' }: { section?: 'rules' | 'documents' }) {
  const qc = useQueryClient();
  const q = useGet<any>('owner-settings', '/school/settings');
  const [s, setS] = useState<Record<string, any> | null>(null);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (q.data) setS({ ...(q.data.defaults ?? {}), ...(q.data.settings ?? {}), document_fees: { ...(q.data.defaults?.document_fees ?? {}), ...(q.data.settings?.document_fees ?? {}) } });
  }, [q.data]);
  const cardTitle = section === 'documents' ? 'Fees, transcripts & documents' : 'School rules';
  if (q.isLoading) return <Card title={cardTitle}><p className="text-sm text-slate-500">Loading…</p></Card>;
  if (q.error) return <Card title={cardTitle}><Notice kind="error">{errMsg(q.error)}</Notice><Button variant="ghost" onClick={() => q.refetch()}>Retry</Button></Card>;
  if (!s) return null;
  const set = (k: string, v: unknown) => setS((x) => ({ ...(x ?? {}), [k]: v }));
  const bool = (k: string, l: string, hint?: string) => (
    <label className="flex items-start gap-2 text-sm"><input className="mt-1" type="checkbox" checked={!!s[k]} onChange={(e) => set(k, e.target.checked)} /><span>{l}{hint && <span className="block text-xs text-slate-400">{hint}</span>}</span></label>
  );
  async function save() {
    setBusy(true);
    setMsg(null);
    const { sibling_discount_id: _ignored, ...rest } = s ?? {};
    const settings = {
      ...rest,
      promotion_pass_average: Number(rest.promotion_pass_average),
      fee_reminder_days_before: Number(rest.fee_reminder_days_before),
      document_fees: Object.fromEntries(DOC_FEES.map(([k]) => [k, Number(rest.document_fees?.[k] ?? 0)])),
    };
    try {
      const r = await call('put', '/school/settings', { body: { settings } });
      setMsg({ kind: 'ok', text: r.message ?? 'Settings saved.' });
      qc.invalidateQueries({ queryKey: ['owner-settings'] });
    } catch (e) {
      setMsg({ kind: 'error', text: errMsg(e) });
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card title={cardTitle}>
      {msg && <Notice kind={msg.kind}>{msg.text}</Notice>}
      {section === 'rules' ? (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <h3 className="font-semibold text-sm">Academics</h3>
            <Row label="Pass average for promotion (%)"><input className={inputCls} type="number" min={0} max={100} value={s.promotion_pass_average ?? ''} onChange={(e) => set('promotion_pass_average', e.target.value)} /></Row>
            <Row label="Students are late after"><input className={inputCls} type="time" value={s.attendance_late_after ?? '08:00'} onChange={(e) => set('attendance_late_after', e.target.value)} /></Row>
            {bool('admissions_open', 'Admissions are open')}
          </div>
          <div className="space-y-3">
            <h3 className="font-semibold text-sm">Results & parents</h3>
            {bool('result_hold_on_debt', 'Hold report cards while fees are owed', 'Held results are listed under Assessments & Results → Held Results, where the desk or owner can release them.')}
            {bool('parent_can_message_teachers', 'Parents can message teachers')}
            <h3 className="font-semibold text-sm pt-2">Fee reminders</h3>
            {bool('fee_reminders_enabled', 'Send fee reminders automatically')}
            <Row label="Send reminders this many days before the due date"><input className={inputCls} type="number" min={0} max={60} value={s.fee_reminder_days_before ?? ''} disabled={!s.fee_reminders_enabled} onChange={(e) => set('fee_reminder_days_before', e.target.value)} /></Row>
            <p className="text-xs text-slate-500">Transcript and document prices are under Assessments & Results → Fees & Transcripts.</p>
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <h3 className="font-semibold text-sm">Transcript & document fees (₦)</h3>
            <p className="text-xs text-slate-500 -mt-2">What alumni and students pay when they request these documents. Use 0 for free.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {DOC_FEES.map(([k, l]) => (
                <Row key={k} label={l}><input className={inputCls} type="number" min={0} step={100} value={s.document_fees?.[k] ?? 0} onChange={(e) => set('document_fees', { ...(s.document_fees ?? {}), [k]: e.target.value })} /></Row>
              ))}
            </div>
          </div>
          <div className="space-y-3">
            <h3 className="font-semibold text-sm">Fee reminders</h3>
            {bool('fee_reminders_enabled', 'Send fee reminders automatically')}
            <Row label="Send reminders this many days before the due date"><input className={inputCls} type="number" min={0} max={60} value={s.fee_reminder_days_before ?? ''} disabled={!s.fee_reminders_enabled} onChange={(e) => set('fee_reminder_days_before', e.target.value)} /></Row>
            {s.sibling_discount_id ? <p className="text-xs text-slate-500">A sibling discount rule is active (set under Fees → Discounts).</p> : null}
            <p className="text-xs text-slate-500">Fee items, structures and discounts are managed under Finance → Fees & Invoices.</p>
          </div>
        </div>
      )}
      <div className="mt-5"><Button disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save settings'}</Button></div>
    </Card>
  );
}

const SETTINGS_TABS = [{ id: 'profile', label: 'Profile' }, { id: 'branding', label: 'Branding' }, { id: 'theme', label: 'Report cards' }, { id: 'rules', label: 'School rules' }];
export function SettingsPage() {
  const [params, setParams] = useSearchParams();
  const tab = SETTINGS_TABS.some((t) => t.id === params.get('tab')) ? (params.get('tab') as string) : 'profile';
  const setTab = (t: string) => setParams({ tab: t }, { replace: true });
  return (
    <div>
      <PageHeader title="School settings" sub="Profile, branding, report cards, school rules, and transcript & document fees" />
      <Tabs value={tab} onChange={setTab} tabs={SETTINGS_TABS} />
      {tab === 'profile' && <ProfileForm />}
      {tab === 'branding' && <Branding />}
      {tab === 'theme' && <ResultTheme />}
      {tab === 'rules' && <SchoolSettings />}
      {params.get('tab') === 'documents' && <Navigate to="/owner/fees-transcripts" replace />}
    </div>
  );
}

/* ---------- roles ---------- */
export function RolesPage() {
  const perms = useGet<any>('owner-permissions', '/school/permissions');
  return (
    <div className="space-y-5">
      <PageHeader title="Roles and permissions" sub="Build custom staff roles from the permission catalogue" />
      <RolesPanel />
      <Card title="Permission catalogue">
        {perms.isLoading ? <p className="text-sm text-slate-500">Loading…</p> : perms.error ? <Notice kind="error">{errMsg(perms.error)}</Notice> : !arr(perms.data?.modules).length ? <p className="text-sm text-slate-500">No permissions defined.</p> : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            {arr(perms.data.modules).map((m: any) => (
              <div key={m.module}>
                <div className="text-xs font-semibold uppercase text-slate-500 mb-1">{m.module}</div>
                <ul className="space-y-0.5">{arr(m.permissions).map((p: any) => <li key={p.key}>{p.label} <span className="text-xs text-slate-400">{p.key}</span></li>)}</ul>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

/* ---------- audit log ---------- */
export function AuditLogPage() {
  const [f, setF] = useState({ q: '', action: '', role: '', from: '', to: '' });
  const list = useList('owner-audit', '/school/audit-logs', f);
  const [open, setOpen] = useState<any>(null);
  const set = (k: keyof typeof f) => (v: string) => setF((x) => ({ ...x, [k]: v }));
  return (
    <div>
      <PageHeader title="Audit log" sub="Every sensitive action taken in your school" />
      <Card>
        <Filters>
          <Search value={f.q} onChange={set('q')} placeholder="Search" />
          <Search value={f.action} onChange={set('action')} placeholder="Action, e.g. wallet." />
          <Sel value={f.role} onChange={set('role')} placeholder="Any role" options={['owner', 'bursar', 'desk', 'teacher', 'parent', 'system'].map((r) => ({ value: r, label: r[0].toUpperCase() + r.slice(1) }))} />
          <DateInput label="From" value={f.from} onChange={set('from')} />
          <DateInput label="To" value={f.to} onChange={set('to')} />
        </Filters>
        <DataTable rows={list.rows} loading={list.isLoading} error={list.error} empty="No audit entries match." onRow={setOpen}
          cols={[
            { head: 'When', cell: (r: any) => dt(r.created_at) },
            { head: 'Who', cell: (r: any) => <span>{r.user?.name ?? 'System'}{r.role && <span className="block text-xs text-slate-500">{r.role}</span>}</span> },
            { head: 'Action', cell: (r: any) => <code className="text-xs">{r.action}</code> },
            { head: 'Record', cell: (r: any) => (r.subject_type ? `${String(r.subject_type).split('\\').pop()} #${r.subject_id}` : '—') },
            { head: 'IP', cell: (r: any) => r.ip ?? '—' },
          ]} />
        <Pager meta={list.meta} page={list.page} onPage={list.setPage} />
      </Card>
      {open && (
        <Modal title={open.action} onClose={() => setOpen(null)} wide>
          <div className="text-sm">
            <KV k="When" v={dt(open.created_at)} />
            <KV k="Who" v={`${open.user?.name ?? 'System'}${open.role ? ` (${open.role})` : ''}`} />
            {open.subject_type && <KV k="Record" v={`${String(open.subject_type).split('\\').pop()} #${open.subject_id}`} />}
            <KV k="IP" v={open.ip ?? '—'} />
            {open.user_agent && <KV k="Device" v={<span className="text-xs">{open.user_agent}</span>} />}
            <div className="grid md:grid-cols-2 gap-3 mt-3">
              <div><div className="text-xs text-slate-500 mb-1">Before</div><pre className="bg-slate-50 rounded-xl p-2 text-xs overflow-auto max-h-64">{open.before ? JSON.stringify(open.before, null, 2) : '—'}</pre></div>
              <div><div className="text-xs text-slate-500 mb-1">After</div><pre className="bg-slate-50 rounded-xl p-2 text-xs overflow-auto max-h-64">{open.after ? JSON.stringify(open.after, null, 2) : '—'}</pre></div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ---------- subscription + platform invoices ---------- */
function InvoiceDetail({ id, onClose, onPay }: { id: number; onClose: () => void; onPay: (inv: any) => void }) {
  const q = useGet<any>('owner-platform-invoice', `/school/platform-invoices/${id}`);
  const i = q.data;
  return (
    <Modal title={i ? `Invoice ${i.number}` : 'Invoice'} onClose={onClose} wide>
      {q.isLoading && <p className="text-sm text-slate-500">Loading…</p>}
      {q.error && <Notice kind="error">{errMsg(q.error)}</Notice>}
      {i && (
        <div className="text-sm space-y-3">
          <div><KV k="Period" v={i.period_label} /><KV k="Billable students" v={i.billable_students} /><KV k="Due" v={dOnly(i.due_date)} /><KV k="Status" v={<StatusBadge status={i.status} />} /></div>
          <DataTable rows={arr(i.items)} empty="No line items." cols={[{ head: 'Item', cell: (r: any) => r.description }, { head: 'Qty', right: true, cell: (r: any) => r.quantity }, { head: 'Unit', right: true, cell: (r: any) => money(r.unit_price) }, { head: 'Amount', right: true, cell: (r: any) => money(r.amount) }]} />
          <div><KV k="Subtotal" v={money(i.subtotal)} /><KV k="Credits" v={money(i.credits_applied)} /><KV k="Total" v={money(i.total)} /><KV k="Paid" v={money(i.amount_paid)} /><KV k="Balance" v={<b>{money(i.balance)}</b>} /></div>
          {!!arr(i.payments).length && <DataTable rows={arr(i.payments)} cols={[{ head: 'Paid', cell: (r: any) => dt(r.paid_at) }, { head: 'Reference', cell: (r: any) => r.reference }, { head: 'Method', cell: (r: any) => r.method }, { head: 'Amount', right: true, cell: (r: any) => money(r.amount) }, { head: 'Status', cell: (r: any) => <StatusBadge status={r.status} /> }]} />}
          {i.can_pay && <div className="flex justify-end"><Button onClick={() => onPay(i)}>Pay from wallet</Button></div>}
        </div>
      )}
    </Modal>
  );
}

export function SubscriptionPage() {
  const qc = useQueryClient();
  const sub = useGet<any>('owner-subscription', '/school/subscription');
  const invoices = useList('owner-platform-invoices', '/school/platform-invoices');
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [autoForm, setAutoForm] = useState<null | { enabled: boolean; max: string }>(null);
  const [pay, setPay] = useState<null | { inv: any; amount: string }>(null);
  const [pin, setPin] = useState<null | ((p: string) => Promise<void>)>(null);
  const [view, setView] = useState<number | null>(null);
  const payKeys = useRef(new Map<number, string>());
  const refresh = () => { qc.invalidateQueries({ queryKey: ['owner-subscription'] }); qc.invalidateQueries({ queryKey: ['owner-platform-invoices'] }); qc.invalidateQueries({ queryKey: ['owner-platform-invoice'] }); qc.invalidateQueries({ queryKey: ['school-wallet'] }); };
  const d = sub.data;
  const s = d?.subscription;

  const runWithPin = (fn: (p: string) => Promise<void>) => setPin(() => fn);
  const auto = (enabled: boolean, max: string) => runWithPin(async (p) => {
    const r = await call('post', '/school/subscription/auto-renew', { body: { enabled, max_amount: enabled && max ? Number(max) : null }, pin: p });
    setMsg({ kind: 'ok', text: r.message ?? 'Saved.' });
    refresh();
  });
  const payInvoice = (inv: any, amount: string) => runWithPin(async (p) => {
    if (!payKeys.current.has(inv.id)) payKeys.current.set(inv.id, newKey());
    const r = await call('post', `/school/platform-invoices/${inv.id}/pay`, { body: amount ? { amount: Number(amount) } : {}, pin: p, idemKey: payKeys.current.get(inv.id) });
    payKeys.current.delete(inv.id);
    setMsg({ kind: 'ok', text: `${r.message ?? 'Invoice paid.'} Wallet balance ${money(r.data?.wallet_balance)}.` });
    refresh();
  });

  return (
    <div className="space-y-5">
      <PageHeader title="Subscription" sub="Your OJP plan, renewals and invoices" />
      {msg && <Notice kind={msg.kind}>{msg.text}</Notice>}
      {sub.isLoading ? <Card><p className="text-sm text-slate-500">Loading…</p></Card> : sub.error ? <Card><Notice kind="error">{errMsg(sub.error)}</Notice><Button variant="ghost" onClick={() => sub.refetch()}>Retry</Button></Card> : (
        <div className="grid lg:grid-cols-3 gap-4">
          <Card title="Plan">
            {d?.plan ? (
              <div className="text-sm">
                <div className="text-lg font-bold">{d.plan.name}</div>
                <KV k="Billing" v={d.plan.billing_model === 'flat' ? `${money(d.plan.flat_price)} flat` : `${money(d.plan.price_per_student)} per student`} />
                {d.plan.min_students ? <KV k="Minimum students" v={d.plan.min_students} /> : null}
                {d.plan.student_limit ? <KV k="Student limit" v={d.plan.student_limit} /> : null}
                <KV k="Billable students now" v={d.billable_students} />
              </div>
            ) : <p className="text-sm text-slate-500">No plan assigned yet. OJP will set one up.</p>}
          </Card>
          <Card title="Current period">
            {s ? (
              <div className="text-sm">
                <div className="mb-2"><StatusBadge status={s.status} /></div>
                <KV k="Period" v={`${dOnly(s.period_starts_on)} – ${dOnly(s.period_ends_on)}`} />
                <KV k="Days left" v={s.days_left} />
                <KV k="Students billed" v={s.active_students} />
                <KV k="Amount" v={money(s.amount)} />
                {d?.next_renewal_estimate && <KV k="Next renewal (estimate)" v={`${money(d.next_renewal_estimate.amount)} for ${d.next_renewal_estimate.students} students`} />}
                <KV k="Outstanding to OJP" v={<b>{money(d?.outstanding)}</b>} />
              </div>
            ) : <p className="text-sm text-slate-500">No active subscription period.</p>}
          </Card>
          <Card title="Auto-renewal">
            <div className="text-sm space-y-2">
              <div className="flex items-center gap-2">Status: <StatusBadge status={d?.auto_renew?.enabled ? 'active' : d?.auto_renew?.status ?? 'off'} /></div>
              {d?.auto_renew?.max_amount != null && <KV k="Limit per renewal" v={money(d.auto_renew.max_amount)} />}
              {d?.auto_renew?.last_run_at && <KV k="Last run" v={dt(d.auto_renew.last_run_at)} />}
              {!!d?.auto_renew?.failure_count && <Notice kind="error">{d.auto_renew.failure_count} recent renewal attempt(s) failed. Check the wallet balance.</Notice>}
              <p className="text-xs text-slate-500">When on, OJP takes each renewal from the school wallet.</p>
              {d?.auto_renew?.enabled
                ? <Button variant="ghost" onClick={() => auto(false, '')}>Turn off</Button>
                : <Button onClick={() => setAutoForm({ enabled: true, max: '' })}>Turn on</Button>}
            </div>
          </Card>
        </div>
      )}

      <Card title="OJP invoices">
        <DataTable rows={invoices.rows} loading={invoices.isLoading} error={invoices.error} empty="No invoices from OJP yet." onRow={(r: any) => setView(r.id)}
          cols={[
            { head: 'Invoice', cell: (r: any) => r.number },
            { head: 'Period', cell: (r: any) => r.period_label },
            { head: 'Due', cell: (r: any) => dOnly(r.due_date) },
            { head: 'Total', right: true, cell: (r: any) => money(r.total) },
            { head: 'Balance', right: true, cell: (r: any) => money(r.balance) },
            { head: 'Status', cell: (r: any) => <StatusBadge status={r.status} /> },
            { head: '', cell: (r: any) => (r.can_pay ? <Link2 onClick={() => setPay({ inv: r, amount: '' })}>Pay</Link2> : null) },
          ]} />
        <Pager meta={invoices.meta} page={invoices.page} onPage={invoices.setPage} />
      </Card>

      {view != null && <InvoiceDetail id={view} onClose={() => setView(null)} onPay={(inv) => { setView(null); setPay({ inv, amount: '' }); }} />}
      {autoForm && (
        <Modal title="Turn on auto-renewal" onClose={() => setAutoForm(null)}>
          <div className="space-y-3 text-sm">
            <Row label="Maximum per renewal (optional, ₦)" hint="Leave blank for no limit."><input className={inputCls} inputMode="decimal" value={autoForm.max} onChange={(e) => setAutoForm({ ...autoForm, max: e.target.value.replace(/[^\d.]/g, '') })} /></Row>
            <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setAutoForm(null)}>Cancel</Button><Button onClick={() => { const m = autoForm.max; setAutoForm(null); auto(true, m); }}>Continue with PIN</Button></div>
          </div>
        </Modal>
      )}
      {pay && (
        <Modal title={`Pay invoice ${pay.inv.number}`} onClose={() => setPay(null)}>
          <div className="space-y-3 text-sm">
            <p>Outstanding: <b>{money(pay.inv.balance)}</b>. The money is taken from the school wallet.</p>
            <Row label="Amount (leave blank to pay it all)"><input className={inputCls} inputMode="decimal" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value.replace(/[^\d.]/g, '') })} /></Row>
            <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setPay(null)}>Cancel</Button><Button disabled={!!pay.amount && (Number(pay.amount) <= 0 || Number(pay.amount) > Number(pay.inv.balance))} onClick={() => { const p = pay; setPay(null); payInvoice(p.inv, p.amount); }}>Pay with PIN</Button></div>
          </div>
        </Modal>
      )}
      {pin && (
        <PinDialog
          onCancel={() => setPin(null)}
          onConfirm={async (p) => {
            const fn = pin;
            setPin(null);
            try {
              await fn(p);
            } catch (e) {
              setMsg({ kind: 'error', text: errMsg(e) });
            }
          }}
        />
      )}
    </div>
  );
}
